import type { JSX } from 'preact'
import { useId, useState } from 'preact/hooks'
import { ChartNoAxesColumn, CircleCheckBig } from 'lucide-preact'
import { stayText } from '@/webinarRoom/polls/pollStay'
import { formatPollTime } from './roomPolls'
import '@/webinarRoom/room.css'

interface RoomPollPreviewProps {
    question: string
    options: readonly string[]
    /** When it pops up, or null while the time is not usable yet. */
    offsetSeconds: number | null
    /** How long it stays up; null until the next poll. */
    staysUpSeconds: number | null
    /** The brand's primary colour, set as the skin's `--room-acc`, or null for the room's neutral slate. */
    accent: string | null
}

/**
 * A poll as viewers see it in the room: the room's own poll card in its light skin, whatever theme the app is in, so
 * every colour comes from room.css as it does on the viewer page. Answers here go nowhere.
 */
export function RoomPollPreview({ question, options, offsetSeconds, staysUpSeconds, accent }: RoomPollPreviewProps) {
    const [picked, setPicked] = useState<number | null>(null)
    const [submitted, setSubmitted] = useState(false)
    const questionId = useId()
    const at = offsetSeconds === null ? 'its time' : formatPollTime(offsetSeconds)
    const skin = { '--room-acc': accent ?? undefined, background: 'var(--v-sunken)' } as JSX.CSSProperties

    return (
        <div className="grid gap-4 sm:grid-cols-[minmax(0,340px)_minmax(0,1fr)] items-start">
            <div class="vskin p-4 rounded-xl border border-slate-200 dark:border-slate-700" style={skin}>
                <div class="vp-card">
                    <div class="vp-head"><span class="vbadge vb-poll"><ChartNoAxesColumn size={12} class="ic" aria-hidden="true" />Poll</span></div>
                    <p class="vp-q" id={questionId}>{question}</p>
                    {submitted ? (
                        <p class="vp-thanks"><CircleCheckBig size={14} class="ic" aria-hidden="true" />Thanks, your answer is in.</p>
                    ) : (
                        <>
                            <div class="vp-opts" role="radiogroup" aria-labelledby={questionId}>
                                {options.map((option, index) => (
                                    <button type="button" class="opt" role="radio" aria-checked={picked === index} key={index} onClick={() => setPicked(index)}>
                                        <span class="radio" />{option}
                                    </button>
                                ))}
                            </div>
                            <div class="vp-foot">
                                <button type="button" class="vbtn vbtn-primary vbtn-sm" disabled={picked === null} onClick={() => setSubmitted(true)}>Submit</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <p className="pt-1 text-sm text-slate-600 dark:text-slate-300">
                {staysUpSeconds === null
                    ? `Shows in the chat panel at ${at} until the moderator closes it or the next poll replaces it.`
                    : `Shows in the chat panel at ${at} for ${stayText(staysUpSeconds)}, or until the moderator closes it.`}
                {' '}Answers here aren’t counted.
            </p>
        </div>
    )
}
